import { Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import api from "../api/client";
import type { LeaveRecalculation } from "../types/api";
import { apiErrorMessage } from "../utils/apiError";
import { formatMinutes } from "../utils/format";
import { ErrorNotice, LoadingState, SuccessNotice } from "./Feedback";
import LeaveRecalculationPrompt from "./LeaveRecalculationPrompt";

type LeavePolicy = {
  public_holidays_are_non_working: boolean;
  optional_holidays_are_non_working: boolean;
  allow_negative_leave_balance: boolean;
  allow_negative_adv_balance: boolean;
  allow_negative_overtime_balance: boolean;
  combine_atv_with_leave: boolean;
  rounding_minutes: number;
  school_region: "north" | "middle" | "south";
  school_holidays_enabled: boolean;
};

export default function OrganizationPolicySettings({
  organizationId,
  canManage,
}: {
  organizationId: number;
  canManage: boolean;
}) {
  const [policy, setPolicy] = useState<LeavePolicy | null>(null);
  // Wat er op de server staat, zodat we een echte wisseling van de pot
  // herkennen en niet iedere opslag als wijziging behandelen.
  const [savedPool, setSavedPool] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [recalculationToken, setRecalculationToken] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get<LeavePolicy>(
        `/organizations/${organizationId}/leave-policy/`,
      );
      setPolicy(response.data);
      setSavedPool(response.data.combine_atv_with_leave);
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "Het verlofbeleid kon niet worden geladen."),
      );
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!policy) return;
    // De pot bepaalt van welk saldo goedgekeurd verlof afgaat. Verandert die,
    // dan wordt bestaand verlof meteen opnieuw geboekt; dat hoort niet
    // ongemerkt te gebeuren.
    if (policy.combine_atv_with_leave !== savedPool) {
      const affected = await api
        .get<LeaveRecalculation>(`/leave/recalculation/?organization=${organizationId}`)
        .then((response) => response.data.years)
        .catch(() => []);
      const entryCount = affected.reduce((sum, row) => sum + row.entry_count, 0);
      if (entryCount > 0) {
        const totalMinutes = affected.reduce((sum, row) => sum + row.minutes, 0);
        const confirmed = window.confirm(
          `${
            policy.combine_atv_with_leave ? "Samenvoegen" : "Splitsen"
          } van ATV en verlof verandert van welk saldo goedgekeurd verlof afgaat. ` +
            `${entryCount} verlofregel(s) van in totaal ${formatMinutes(
              totalMinutes,
            )} in ${affected.map((row) => row.year).join(", ")} ` +
            "worden meteen opnieuw geboekt. Doorgaan?",
        );
        if (!confirmed) return;
      }
    }
    setWorking(true);
    setError("");
    try {
      const response = await api.patch<
        LeavePolicy & { pool_recalculation?: { entries: number; years: number[] } }
      >(`/organizations/${organizationId}/leave-policy/`, policy);
      setPolicy(response.data);
      setSavedPool(response.data.combine_atv_with_leave);
      const rebooked = response.data.pool_recalculation?.entries ?? 0;
      setSuccess(
        "Verlofbeleid opgeslagen." +
          (rebooked
            ? ` ${rebooked} goedgekeurde verlofregel(s) zijn opnieuw geboekt.`
            : ""),
      );
      setRecalculationToken((value) => value + 1);
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "Het verlofbeleid kon niet worden opgeslagen."),
      );
    } finally {
      setWorking(false);
    }
  };

  if (loading) return <LoadingState label="Verlofbeleid laden..." />;
  if (!policy) return error ? <ErrorNotice>{error}</ErrorNotice> : null;

  return (
    <>
      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}
      <section className="surface settings-section policy-settings">
        <div className="settings-heading">
          <h2>Negatief saldo</h2>
          <p>Deze regels worden gecontroleerd wanneer verlof wordt goedgekeurd.</p>
        </div>
        {!canManage && (
          <div className="notice notice-info compact">
            Alleen een eigenaar of beheerder kan deze regels wijzigen.
          </div>
        )}
        <div className="policy-toggle-list">
          <PolicyToggle
            label="Verlof"
            detail="Regulier vakantieverlof mag onder nul komen."
            checked={policy.allow_negative_leave_balance}
            disabled={!canManage}
            onChange={(checked) =>
              setPolicy({ ...policy, allow_negative_leave_balance: checked })
            }
          />
          <PolicyToggle
            label="ATV"
            detail="Opname van ATV-uren mag onder nul komen."
            checked={policy.allow_negative_adv_balance}
            disabled={!canManage}
            onChange={(checked) =>
              setPolicy({ ...policy, allow_negative_adv_balance: checked })
            }
          />
          <PolicyToggle
            label="Overuren"
            detail="Tijd-voor-tijd mag meer opnemen dan het opgebouwde overurensaldo."
            checked={policy.allow_negative_overtime_balance}
            disabled={!canManage}
            onChange={(checked) =>
              setPolicy({ ...policy, allow_negative_overtime_balance: checked })
            }
          />
        </div>
        {canManage && (
          <button
            className="button primary policy-save"
            type="button"
            disabled={working}
            onClick={() => void save()}
          >
            <Save size={17} /> {working ? "Opslaan..." : "Beleid opslaan"}
          </button>
        )}
      </section>
      <section className="surface settings-section leave-pool-settings">
        <div className="settings-heading">
          <h2>ATV en verlof</h2>
          <p>
            Sommige werkgevers zien ATV en verlof als een gezamenlijke pot uren. Staat
            dit aan, dan kiest een medewerker bij het plannen niet meer zelf: opgenomen
            uren gaan eerst van het ATV-saldo af en daarna van het verlofsaldo.
          </p>
        </div>
        <div className="policy-toggle-list">
          <PolicyToggle
            label="ATV en verlof als een pot"
            detail="Opgenomen uren gaan eerst van ATV af en daarna van het verlofsaldo."
            checked={policy.combine_atv_with_leave}
            disabled={!canManage}
            onChange={(checked) =>
              setPolicy({ ...policy, combine_atv_with_leave: checked })
            }
          />
        </div>
        {canManage && (
          <button
            className="button primary policy-save"
            type="button"
            disabled={working}
            onClick={() => void save()}
          >
            <Save size={17} /> {working ? "Opslaan..." : "Beleid opslaan"}
          </button>
        )}
      </section>
      <LeaveRecalculationPrompt
        query={`organization=${organizationId}`}
        reloadToken={recalculationToken}
        canManage={canManage}
      />
    </>
  );
}

function PolicyToggle({
  label,
  detail,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  detail: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="toggle-field policy-toggle">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        <strong>{label}</strong>
        <small>{detail}</small>
      </span>
    </label>
  );
}
